/**
 * Where the Cinch AI bubble sits (Justin, 2026-10-29): as {left, bottom} in px from the viewport's left
 * and bottom edges. Shared by the bubble and the chat window: the window opens from the bubble, and when
 * it shrinks back the bubble appears at the window's nearest corner.
 */

export const BUBBLE_SIZE = 56;

/** localStorage key for the bubble's saved spot and hint state, per user (the provider's `userKey`). */
export const bubbleStorageKey = userKey => `${userKey}.bubble`;

/** A saved spot; positions saved before 2026-10-29 were distances from the right edge. */
export function bubbleSpotFromSaved(saved, viewportWidth = window.innerWidth) {
  if (typeof saved?.left === 'number' && typeof saved?.bottom === 'number') return {
    left: saved.left,
    bottom: saved.bottom
  };
  if (typeof saved?.right === 'number' && typeof saved?.bottom === 'number') {
    return {
      left: viewportWidth - BUBBLE_SIZE - saved.right,
      bottom: saved.bottom
    };
  }
  return null;
}

/** The bubble's spot at the window's corner nearest the viewport corner the window is closest to. */
export function bubbleSpotAtCorner(rect, viewportWidth = window.innerWidth, viewportHeight = window.innerHeight) {
  const onLeft = rect.left + rect.width / 2 < viewportWidth / 2;
  const onBottom = rect.top + rect.height / 2 >= viewportHeight / 2;
  return {
    left: Math.round(onLeft ? rect.left : rect.right - BUBBLE_SIZE),
    bottom: Math.round(onBottom ? viewportHeight - rect.bottom : viewportHeight - rect.top - BUBBLE_SIZE)
  };
}

/** A saved window spot ({left, bottom}) kept on screen for a window of this size (the viewport may have shrunk). */
export function clampWindowSpot(spot, width, height, viewportWidth = window.innerWidth, viewportHeight = window.innerHeight) {
  if (typeof spot?.left !== 'number' || typeof spot?.bottom !== 'number') return null;
  return {
    left: Math.round(Math.min(Math.max(8, spot.left), Math.max(8, viewportWidth - width - 8))),
    bottom: Math.round(Math.min(Math.max(8, spot.bottom), Math.max(8, viewportHeight - height - 8)))
  };
}

/**
 * Where a window of this size opens from the bubble: its own corner on the bubble's corner, kept on screen.
 * The mirror of bubbleSpotAtCorner, so minimizing and reopening puts the window back where it was.
 */
export function windowSpotFromBubble(bubbleRect, width, height, viewportWidth = window.innerWidth, viewportHeight = window.innerHeight) {
  const onLeft = bubbleRect.left + bubbleRect.width / 2 < viewportWidth / 2;
  const onBottom = bubbleRect.top + (bubbleRect.height ?? bubbleRect.width) / 2 >= viewportHeight / 2;
  const left = onLeft ? bubbleRect.left : bubbleRect.right - width;
  // A bubble in the top half opens the window downward (tops aligned); in the bottom half, upward.
  const bottom = onBottom ? viewportHeight - bubbleRect.bottom : viewportHeight - bubbleRect.top - height;
  return {
    left: Math.round(Math.min(Math.max(8, left), Math.max(8, viewportWidth - width - 8))),
    bottom: Math.round(Math.min(Math.max(8, bottom), Math.max(8, viewportHeight - height - 8)))
  };
}